import { useDispatch } from 'react-redux';
import './css/app.css';
import './css/common.css';
import { useState, useEffect, useCallback } from 'react';
import { change_Current_Location, changeHeaderTitle } from '../store';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

import dayjs from 'dayjs';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

import List from './component/list/list';
import { LoadingModal, MessageModal } from './component/modal/modal';
import { CountAmountLeft, DownloadBtn } from './component/btn/btn';

const RESOURCE_LABEL = {
    MERCHANT: '가맹점',
};

const isChangeAction = (actionType) => {
    if (!actionType) return false;
    const t = String(actionType).toUpperCase();
    return t === 'POST' || t === 'PUT' || t === 'DELETE' || t === 'PATCH';
};

function StatusBadge({ code }) {
    if (code == null) return <span style={{ color: '#999' }}>-</span>;
    let color = '#666', bg = '#f0f0f0';
    if (code >= 200 && code < 300) { color = '#0a6e0a'; bg = '#e6f4ea'; }
    else if (code >= 300 && code < 400) { color = '#888'; bg = '#f0f0f0'; }
    else if (code >= 400 && code < 500) { color = '#b76e00'; bg = '#fff4e5'; }
    else if (code >= 500) { color = '#c62828'; bg = '#fde7e7'; }
    return (
        <span style={{
            color, background: bg, padding: '2px 8px', borderRadius: '10px',
            fontSize: '11px', fontWeight: 600, display: 'inline-block', minWidth: '34px', textAlign: 'center',
        }}>
            {code}
        </span>
    );
}

const formatDateTime = (value) => {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format('YY-MM-DD HH:mm:ss') : String(value);
};

const safeJson = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') {
        try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; }
    }
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

function AuditLog() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);

    const resourceType = searchParams.get('resourceType') ?? '';
    const resourceId = searchParams.get('resourceId') ?? '';

    const isMerchantMode = resourceType === 'MERCHANT' && !!resourceId;

    useEffect(() => {
        dispatch(change_Current_Location('감사 로그'));
        dispatch(changeHeaderTitle({ mainTitle: '관리자 메뉴', subTitle: '감사 로그' }));
        const t = setTimeout(() => setLoading(false), 300);
        return () => clearTimeout(t);
    }, [dispatch]);

    const clearResource = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('resourceType');
        next.delete('resourceId');
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="content" id="contents">
            {loading ? <LoadingModal /> : ''}
            {isMerchantMode ? (
                <MerchantAuditView
                    mid={resourceId}
                    onClearResource={clearResource}
                />
            ) : (
                <StubView />
            )}
        </div>
    );
}

function StubView() {
    return (
        <div className="intro-y box intro-padding" style={{ padding: '40px', textAlign: 'center' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
                전체 감사 로그 조회는 준비 중입니다
            </h2>
            <p style={{ color: '#555', lineHeight: 1.6, marginBottom: '20px' }}>
                현재는 <b>특정 가맹점별 변경 이력</b>만 지원합니다.<br />
                <b>가맹점 관리</b> &gt; 가맹점 선택 &gt; <b>변경 이력</b> 버튼으로 진입해주세요.
            </p>
            <p style={{ color: '#999', fontSize: '12px' }}>
                글로벌 조회 API가 준비되면 이 화면에 자유 필터 조회가 활성화됩니다.
            </p>
        </div>
    );
}

export function MerchantAuditView({ mid, onClearResource, showFilterChip = true, detailMode = 'modal' }) {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [combineData, setCombineData] = useState([]);
    const [clickData, setClickData] = useState(null);
    const [page, setPage] = useState(1);
    const [maxFirstPage] = useState(20);

    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [message, setMessage] = useState('');
    const [changesOnly, setChangesOnly] = useState(true);

    const fetchPage = useCallback(async (cursor = null, append = false) => {
        setSpinner(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/admin-logs/entities/merchant/${encodeURIComponent(mid)}`,
                {
                    withCredentials: true,
                    params: {
                        limit: maxFirstPage,
                        ...(cursor ? { before_id: cursor } : {}),
                    },
                }
            );
            const tmp = response?.data;
            const payload = tmp?.data ?? tmp;
            const fetched = payload?.logs ?? [];
            const next = payload?.next_cursor ?? null;

            if (tmp?.code === 9999) {
                navigate('/');
                return;
            }

            setLogs((prev) => (append ? [...prev, ...fetched] : fetched));
            setNextCursor(next);
            setHasMore(!!next);
        } catch (error) {
            console.error(error);
            setMessage({ msg: '감사 로그를 불러오지 못했습니다.' });
        } finally {
            setSpinner(false);
        }
    }, [mid, maxFirstPage, navigate]);

    useEffect(() => {
        setLogs([]);
        setNextCursor(null);
        setHasMore(false);
        setPage(1);
        fetchPage(null, false);
    }, [mid, fetchPage]);

    const tableHeaderData = [
        { no: 1, text: '발생일시', width: '160px', textAlign: 'center' },
        { no: 2, text: '작업자', width: '90px', textAlign: 'center' },
        { no: 3, text: '메서드', width: '70px', textAlign: 'center' },
        { no: 4, text: '상태', width: '70px', textAlign: 'center' },
        { no: 5, text: '엔드포인트', width: '240px', textAlign: 'left', flex: 1 },
        { no: 6, text: 'IP', width: '120px', textAlign: 'center' },
        { no: 7, text: '변경필드', width: '90px', textAlign: 'center' },
    ];

    const visibleLogs = changesOnly ? (logs ?? []).filter((d) => isChangeAction(d.action_type)) : (logs ?? []);

    useEffect(() => {
        const rows = visibleLogs.map((d) => {
            const diffCount = Array.isArray(d.diff) ? d.diff.length : 0;
            const dim = d.status_code === 304;
            const dimStyle = dim ? { color: '#999' } : undefined;
            const rowData = [
                { no: 1, text: formatDateTime(d.timestamp), width: '160px', textAlign: 'center', style: dimStyle },
                { no: 2, text: d.admin_id ?? '', width: '90px', textAlign: 'center', style: dimStyle },
                { no: 3, text: d.action_type ?? '', width: '70px', textAlign: 'center', style: dimStyle },
                { no: 4, text: <StatusBadge code={d.status_code} />, width: '70px', textAlign: 'center' },
                { no: 5, text: d.endpoint ?? '', width: '240px', textAlign: 'left', flex: 1, style: dimStyle },
                { no: 6, text: d.ip_address ?? '', width: '120px', textAlign: 'center', style: dimStyle },
                { no: 7, text: diffCount > 0 ? `${diffCount}건` : '-', width: '90px', textAlign: 'center', style: dimStyle },
            ];
            rowData.id = d.id ?? d.log_id ?? `${d.timestamp}-${d.admin_id}`;
            rowData.raw = d;
            return rowData;
        });
        setCombineData(rows);
    }, [logs, changesOnly]);

    const handleRowClick = (rowArr) => {
        if (rowArr?.raw) setClickData(rowArr.raw);
    };

    const onLoadMore = () => {
        if (nextCursor) fetchPage(nextCursor, true);
    };

    const excelBtn = () => {
        if (!visibleLogs.length) {
            setMessage({ msg: '내보낼 데이터가 없습니다.' });
            return;
        }
        buildExcel(mid, visibleLogs);
    };

    return (
        <>
            <MessageModal message={message} />

            {showFilterChip && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', padding: '8px 4px' }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>활성 필터:</span>
                    <FilterChip
                        label={`대상: ${RESOURCE_LABEL.MERCHANT} / ${mid}`}
                        onClear={onClearResource}
                    />
                </div>
            )}

            <div className="intro-y box intro-padding">
                <div className="tabulator_sorting_wrap" style={{ flexDirection: 'row' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <CountAmountLeft tableBodyData={visibleLogs.length} />
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#444', cursor: 'pointer', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={changesOnly}
                                onChange={(e) => setChangesOnly(e.target.checked)}
                                style={{ cursor: 'pointer' }}
                            />
                            변경 작업만 보기
                            <span style={{ color: '#999' }}>(POST/PUT/DELETE/PATCH)</span>
                        </label>
                        {hasMore && (
                            <span style={{ fontSize: '12px', color: '#999' }}>
                                (더 많은 이력이 있습니다)
                            </span>
                        )}
                    </div>
                    <div className="excel-disable" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DownloadBtn text={'엑셀 다운로드'} onClick={excelBtn} />
                    </div>
                </div>

                <List
                    tableHeaderData={tableHeaderData}
                    tableBodyData={logs.length}
                    filter_tableBodyData={combineData}
                    page={page}
                    setPage={setPage}
                    maxFirstPage={Math.max(maxFirstPage, logs.length || 1)}
                    clickData={clickData}
                    onRowClick={handleRowClick}
                    spinner={spinner}
                    nonePage={false}
                />

                {hasMore && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={onLoadMore}
                            disabled={spinner}
                            style={{ height: '34px', padding: '0 16px', fontSize: '13px' }}
                        >
                            {spinner ? '불러오는 중...' : '더 보기'}
                        </button>
                    </div>
                )}
            </div>

            {clickData && detailMode === 'inline' && (
                <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #e5e5e5', borderRadius: '6px', background: '#fafafa' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: 'bold', margin: 0 }}>감사 로그 상세</h3>
                        <button
                            type="button"
                            onClick={() => setClickData(null)}
                            aria-label="상세 닫기"
                            style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666' }}
                        >
                            ×
                        </button>
                    </div>
                    <AuditLogDetailContent data={clickData} />
                </div>
            )}

            {clickData && detailMode === 'modal' && (
                <AuditLogDetailModal
                    data={clickData}
                    onClose={() => setClickData(null)}
                />
            )}
        </>
    );
}

function FilterChip({ label, onClear }) {
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            background: '#e0ecff',
            color: '#1f4ea1',
            borderRadius: '14px',
            fontSize: '12px',
            border: '1px solid #c5d8f5',
        }}>
            {label}
            <button
                type="button"
                onClick={onClear}
                aria-label="필터 해제"
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#1f4ea1',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '14px',
                    lineHeight: 1,
                }}
            >
                ×
            </button>
        </span>
    );
}

function AuditLogDetailContent({ data }) {
    const diff = Array.isArray(data?.diff) ? data.diff : [];
    const queryParams = data?.additional_info?.query_params;
    const hasQueryParams = queryParams && typeof queryParams === 'object' && Object.keys(queryParams).length > 0;

    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px', fontSize: '13px', marginBottom: '16px' }}>
                <div style={{ color: '#666' }}>발생일시</div><div>{formatDateTime(data.timestamp)}</div>
                <div style={{ color: '#666' }}>작업자</div><div>{data.admin_id ?? ''}</div>
                <div style={{ color: '#666' }}>액션</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>{data.action_type ?? ''}</span>
                    {data.status_code != null && <StatusBadge code={data.status_code} />}
                    <span style={{ color: '#999', wordBreak: 'break-all' }}>{data.endpoint ?? ''}</span>
                </div>
                {data.ip_address && (<>
                    <div style={{ color: '#666' }}>IP</div><div>{data.ip_address}</div>
                </>)}
                {hasQueryParams && (<>
                    <div style={{ color: '#666' }}>쿼리 파라미터</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                        {Object.entries(queryParams).map(([k, v]) => `${k}=${v}`).join(' & ')}
                    </div>
                </>)}
            </div>

            {diff.length > 0 ? (
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>변경 필드</div>
                    <div style={{ overflow: 'auto', border: '1px solid #e5e5e5', borderRadius: '4px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead style={{ background: '#f5f5f5' }}>
                                <tr>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e5e5' }}>필드</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e5e5' }}>변경 전</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e5e5' }}>변경 후</th>
                                </tr>
                            </thead>
                            <tbody>
                                {diff.map((d, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{d.field ?? d.key ?? ''}</td>
                                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', wordBreak: 'break-all' }}>{String(d.before ?? d.old ?? '')}</td>
                                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', wordBreak: 'break-all' }}>{String(d.after ?? d.new ?? '')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div style={{ marginBottom: '12px', color: '#999', fontSize: '12px' }}>
                    변경 비교(diff) 데이터가 아직 제공되지 않습니다. (백엔드 SQL 적용 후 표시 예정)
                </div>
            )}

            <div style={{ marginBottom: '12px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>요청 본문 (request_body)</div>
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', maxHeight: '260px', overflow: 'auto', fontSize: '12px', margin: 0 }}>
{safeJson(data.request_body) || '-'}
                </pre>
            </div>

            {data.response_body != null && (
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>응답 본문 (response_body)</div>
                    <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', maxHeight: '260px', overflow: 'auto', fontSize: '12px', margin: 0 }}>
{safeJson(data.response_body) || '-'}
                    </pre>
                </div>
            )}
        </>
    );
}

function AuditLogDetailModal({ data, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className="modal alert_modal"
            tabIndex="-1"
            style={{
                zIndex: 100001,
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onClick={onClose}
        >
            <div
                className="modal-dialog"
                style={{
                    minWidth: '320px',
                    maxWidth: '900px',
                    width: '95%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    background: '#fff',
                    borderRadius: '8px',
                    padding: '20px',
                    zIndex: 100002,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-center font-bold text-lg" style={{ marginBottom: '12px' }}>감사 로그 상세</h2>
                <AuditLogDetailContent data={data} />
                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn w-20 btn_ok" onClick={onClose}>닫기</button>
                </div>
            </div>
        </div>
    );
}

const buildExcel = (mid, rows) => {
    const excelFileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const excelFileExtension = '.xlsx';
    const excelFileName = `감사로그_${mid}_${dayjs().format('YYYYMMDD_HHmmss')}`;

    const headerRow = [
        'NO', '발생일시', '작업자', '메서드', '상태코드', '엔드포인트', 'IP', '변경필드수', 'request_body', 'response_body', 'diff',
    ];

    const bodyRows = (rows ?? []).map((d, i) => [
        i + 1,
        formatDateTime(d.timestamp),
        d.admin_id ?? '',
        d.action_type ?? '',
        d.status_code ?? '',
        d.endpoint ?? '',
        d.ip_address ?? '',
        Array.isArray(d.diff) ? d.diff.length : 0,
        d.request_body ? (typeof d.request_body === 'string' ? d.request_body : JSON.stringify(d.request_body)) : '',
        d.response_body ? (typeof d.response_body === 'string' ? d.response_body : JSON.stringify(d.response_body)) : '',
        d.diff ? JSON.stringify(d.diff) : '',
    ]);

    const sheetData = [headerRow, ...bodyRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws['!cols'] = Array(headerRow.length).fill({ wpx: 130 });

    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const excelFile = new Blob([excelBuffer], { type: excelFileType });
    FileSaver.saveAs(excelFile, excelFileName + excelFileExtension);
};

export default AuditLog;